import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as subtasksApi from '~/services/api/subtasks';

import type { Subtask, TaskWithSubtasks } from '~/types';

import { logger } from '../utils/logger';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useSubtasks(projectId: string | null, taskId: string | null) {
  const queryClient = useQueryClient();

  // Debug taskId
  logger.debug({ component: 'useSubtasks', projectId, taskId }, 'useSubtasks hook called');

  // Check if we already have subtasks in the tasks cache for this project
  const tasks = queryClient.getQueryData<TaskWithSubtasks[]>(['tasks', projectId]);
  const taskInList = tasks?.find(t => t.id === taskId);
  const subtasksFromCache = taskInList?.subtasks;
  const taskBelongsToProject = !!taskInList; // Task exists in current project's task list

  // Only use cache if subtasks array actually has data (or is explicitly an empty array from API)
  // undefined means subtasks haven't been loaded yet
  const hasSubtasksInCache = subtasksFromCache !== undefined;

  const {
    data: queryData,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['subtasks', projectId, taskId],
    queryFn: (): Promise<Subtask[]> | Subtask[] => {
      logger.debug({ component: 'useSubtasks', projectId, taskId }, 'Fetching subtasks');
      return projectId && taskId ? subtasksApi.getTaskSubtasks(projectId, taskId) : [];
    },
    initialData: subtasksFromCache, // Use cached data if available
    enabled:
      !!(projectId && taskId) &&
      !projectId.startsWith('temp-') &&
      !taskId.startsWith('temp-') &&
      taskBelongsToProject && // Only fetch if task belongs to current project
      !hasSubtasksInCache, // Only fetch if subtasks property doesn't exist (undefined)
  });

  // Use query data, fallback to cache, or empty array
  const subtasks = queryData || subtasksFromCache || [];

  const createSubtask = useMutation({
    mutationFn: ({
      projectId,
      subtask,
      taskId,
    }: {
      projectId: string;
      subtask: Omit<Subtask, 'id' | 'taskId'>;
      taskId: string;
    }) => subtasksApi.createSubtask(projectId, taskId, subtask),
    onSuccess: (newSubtask): void => {
      logger.debug({ component: 'useSubtasks', taskId: newSubtask.taskId }, 'Created new subtask', {
        subtask: newSubtask,
      });
      queryClient.setQueryData(
        ['subtasks', projectId, newSubtask.taskId],
        (old: Subtask[] = []) => {
          const updated = [...old, newSubtask];
          logger.debug(
            { component: 'useSubtasks', taskId: newSubtask.taskId },
            'Updated subtasks cache',
            { subtasks: updated },
          );
          return updated;
        },
      );

      // Update task cache
      const task = queryClient.getQueryData<TaskWithSubtasks>(['task', newSubtask.taskId]);
      if (task) {
        queryClient.setQueryData(['task', newSubtask.taskId], {
          ...task,
          subtasks: [...task.subtasks, newSubtask],
        });
      } else {
        logger.warn({ component: 'useSubtasks' }, 'Task not found in cache when creating subtask', {
          taskId: newSubtask.taskId,
        });
      }
    },
    onError: (error): void => {
      logger.error({ component: 'useSubtasks' }, 'Failed to create subtask', error);
    },
  });

  const updateSubtask = useMutation({
    mutationFn: ({
      projectId,
      subtaskId,
      taskId,
      updates,
    }: {
      projectId: string;
      subtaskId: string;
      taskId: string;
      updates: Partial<Subtask>;
    }) => subtasksApi.updateSubtask(projectId, taskId, subtaskId, updates),
    onSuccess: (updatedSubtask): void => {
      queryClient.setQueryData(
        ['subtasks', projectId, updatedSubtask.taskId],
        (old: Subtask[] = []) =>
          old.map(subtask => (subtask.id === updatedSubtask.id ? updatedSubtask : subtask)),
      );
    },
  });

  const deleteSubtask = useMutation({
    mutationFn: ({
      projectId,
      subtaskId,
      taskId,
    }: {
      projectId: string;
      subtaskId: string;
      taskId: string;
    }) => subtasksApi.deleteSubtask(projectId, taskId, subtaskId),
    onSuccess: (_, { projectId, subtaskId, taskId }): void => {
      // Update the subtasks cache for the specific task
      queryClient.setQueryData(['subtasks', projectId, taskId], (old: Subtask[] = []) =>
        old.filter(s => s.id !== subtaskId),
      );

      // Also invalidate the tasks cache to ensure the task's subtask count is updated
      void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });

      logger.debug({ component: 'useSubtasks' }, 'Subtask deleted and cache updated', {
        projectId,
        taskId,
        subtaskId,
      });
    },
  });

  return {
    subtasks,
    isLoading,
    error,
    createSubtask,
    updateSubtask,
    deleteSubtask,
  };
}
